/**
 * CalculationDetailsPanel - Display detailed calculation/rule explanation
 *
 * Clean, minimal display showing how HofSync reached a conclusion:
 * - Legal basis (combined reference)
 * - Used facts (inputs)
 * - Calculation logic
 * - Result
 * - Evidence links
 */

import React from 'react';
import {
  Database,
  Calculator,
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileText,
  Map,
  Truck,
  User,
  Warehouse,
} from 'lucide-react';
import { ContextPanel } from '../ContextPanel';
import type { CalculationDetails, CalculationEvidence } from '../../types';
import './CalculationDetailsPanel.css';

export interface CalculationDetailsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  details: CalculationDetails | null;
  onEntityClick?: (entityId: string) => void;
  onDocumentClick?: (documentId: string) => void;
  className?: string;
}

const SEVERITY_ICONS: Record<string, React.ComponentType<{ className?: string; size?: string | number }>> = {
  success: CheckCircle,
  warning: AlertTriangle,
  critical: XCircle,
};

const EVIDENCE_ICONS: Record<string, React.ComponentType<{ className?: string; size?: string | number }>> = {
  field: Map,
  equipment: Truck,
  employee: User,
  animal_group: () => <span className="calc-details__icon-emoji">🐷</span>,
  storage: Warehouse,
  document: FileText,
};

export const CalculationDetailsPanel: React.FC<CalculationDetailsPanelProps> = ({
  isOpen,
  onClose,
  details,
  onEntityClick,
  onDocumentClick,
  className = '',
}) => {
  if (!details) return null;

  const ResultIcon = SEVERITY_ICONS[details.result.severity] || CheckCircle;

  const getEvidenceIcon = (evidence: CalculationEvidence) => {
    const Icon = evidence.type === 'document'
      ? FileText
      : EVIDENCE_ICONS[evidence.entityType || 'field'] || Map;
    return <Icon size={14} className="calc-details__evidence-icon" />;
  };

  const handleEvidenceClick = (evidence: CalculationEvidence) => {
    if (evidence.type === 'document') {
      onDocumentClick?.(evidence.id);
    } else {
      onEntityClick?.(evidence.id);
    }
  };

  return (
    <ContextPanel
      isOpen={isOpen}
      onClose={onClose}
      title={details.header.title}
      subtitle={details.header.ruleName}
      width="medium"
      className={`calc-details-panel ${className}`}
    >
      <div className="calc-details">
        {/* Header meta */}
        {(details.header.statusBadge || details.header.timestamp) && (
          <div className="calc-details__header-meta">
            {details.header.statusBadge && (
              <span className="calc-details__status-badge">
                <CheckCircle size={12} />
                {details.header.statusBadge}
              </span>
            )}
            {details.header.timestamp && <span>{details.header.timestamp}</span>}
          </div>
        )}

        {/* Used Facts - simple label/value pairs */}
        <section className="calc-details__section">
          <div className="calc-details__section-header">
            <Database size={16} className="calc-details__section-icon" />
            <h3 className="calc-details__section-title">Verwendete Daten</h3>
          </div>
          <dl className="calc-details__facts-list">
            {details.facts.map((fact, index) => (
              <div key={index} className="calc-details__fact">
                <dt className="calc-details__fact-label">{fact.label}</dt>
                <dd className="calc-details__fact-value">
                  {fact.value}
                  {fact.source && (
                    <span className="calc-details__fact-source">({fact.source})</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Calculation Logic */}
        <section className="calc-details__section">
          <div className="calc-details__section-header">
            <Calculator size={16} className="calc-details__section-icon" />
            <h3 className="calc-details__section-title">Berechnungslogik</h3>
          </div>
          <div className="calc-details__logic-content">
            <code className="calc-details__rule-expression">
              {details.logic.ruleExpression}
            </code>
            <ul className="calc-details__steps">
              {details.logic.steps.map((step, index) => (
                <li
                  key={index}
                  className={`calc-details__step ${step.isDecisive ? 'calc-details__step--decisive' : ''}`}
                >
                  {step.description}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Result */}
        <section
          className={`calc-details__section calc-details__section--result calc-details__section--result-${details.result.severity}`}
        >
          <div className="calc-details__section-header">
            <ResultIcon size={16} className="calc-details__section-icon" />
            <h3 className="calc-details__section-title">Ergebnis</h3>
          </div>
          <div className="calc-details__result-content">
            <span className="calc-details__result-status">{details.result.status}</span>
            {details.result.deadline && (
              <div className="calc-details__result-row">
                <span className="calc-details__result-label">Frist:</span>
                <span className="calc-details__result-value">{details.result.deadline}</span>
              </div>
            )}
            {details.result.deviation && (
              <div className="calc-details__result-row">
                <span className="calc-details__result-label">Abweichung:</span>
                <span className="calc-details__result-value calc-details__result-value--deviation">
                  {details.result.deviation}
                </span>
              </div>
            )}
          </div>
        </section>

        {/* Evidence */}
        {details.evidence && details.evidence.length > 0 && (
          <section className="calc-details__section">
            <div className="calc-details__section-header">
              <FileText size={16} className="calc-details__section-icon" />
              <h3 className="calc-details__section-title">Nachweise</h3>
            </div>
            <ul className="calc-details__evidence-list">
              {details.evidence.map((evidence, index) => (
                <li key={index}>
                  <button
                    type="button"
                    className="calc-details__evidence-button"
                    onClick={() => handleEvidenceClick(evidence)}
                  >
                    {getEvidenceIcon(evidence)}
                    <span className="calc-details__evidence-name">{evidence.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </ContextPanel>
  );
};

export default CalculationDetailsPanel;
